import { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/Layout/AdminLayout';
import { api } from '../../services/api';
import type { AdminInviteEntry, AuditLogEntry, StaffInviteEntry } from '@vedjegy/shared';
import { ROLE_LABELS } from '@vedjegy/shared';

type Tab = 'invites' | 'staff' | 'audit';

const STATUS_LABEL: Record<string, { label: string; color: string; bg: string }> = {
  pending:  { label: 'Függőben',   color: '#92400e', bg: '#fffbeb' },
  accepted: { label: 'Elfogadva',  color: '#166534', bg: '#f0fdf4' },
  expired:  { label: 'Lejárt',     color: '#6b7280', bg: '#f3f4f6' },
};

const EVENT_LABEL: Record<string, string> = {
  registration_ok:      '✓ Regisztráció',
  registration_fail:    '✗ Regisztráció hiba',
  invite_sent:          '→ Meghívó elküldve',
  invite_accepted:      '✓ Meghívó elfogadva',
};

function fmt(iso: string) {
  return new Date(iso).toLocaleString('hu-HU', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function AdminInvitesPage() {
  const [tab, setTab]               = useState<Tab>('invites');

  // Company invites
  const [invites, setInvites]             = useState<AdminInviteEntry[]>([]);
  const [statusFilter, setStatusFilter]   = useState<string>('all');
  const [totalInvites, setTotalInvites]   = useState(0);

  // Staff invites
  const [staffInvites, setStaffInvites]         = useState<StaffInviteEntry[]>([]);
  const [staffStatusFilter, setStaffStatusFilter] = useState<string>('all');
  const [totalStaff, setTotalStaff]             = useState(0);
  const [staffEmail, setStaffEmail]             = useState('');
  const [staffRole, setStaffRole]               = useState<'tenant_admin' | 'reviewer' | 'editor'>('reviewer');
  const [staffSending, setStaffSending]         = useState(false);
  const [staffSendError, setStaffSendError]     = useState('');
  const [staffSendSuccess, setStaffSendSuccess] = useState('');
  const [staffInviteLink, setStaffInviteLink]   = useState('');
  const [staffLinkCopied, setStaffLinkCopied]   = useState(false);

  // Audit
  const [auditLog, setAuditLog]     = useState<AuditLogEntry[]>([]);
  const [totalAudit, setTotalAudit] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const loadInvites = useCallback(async (status: string) => {
    setLoading(true); setError('');
    try {
      const res = await api.adminInvites.list({ status: status === 'all' ? undefined : status, limit: 200 });
      setInvites(res.invites);
      setTotalInvites(res.total);
    } catch { setError('Nem sikerült betölteni a meghívókat.'); }
    finally { setLoading(false); }
  }, []);

  const loadStaffInvites = useCallback(async (status: string) => {
    setLoading(true); setError('');
    try {
      const res = await api.staffInvite.list({ status: status === 'all' ? undefined : status, limit: 200 });
      setStaffInvites(res.invites);
      setTotalStaff(res.total);
    } catch { setError('Nem sikerült betölteni a munkatársi meghívókat.'); }
    finally { setLoading(false); }
  }, []);

  const loadAudit = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await api.adminAuditLog.list({ limit: 200 });
      setAuditLog(res.entries);
      setTotalAudit(res.total);
    } catch { setError('Nem sikerült betölteni a naplót.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (tab === 'invites') void loadInvites(statusFilter);
    else if (tab === 'staff') void loadStaffInvites(staffStatusFilter);
    else void loadAudit();
  }, [tab, statusFilter, staffStatusFilter, loadInvites, loadStaffInvites, loadAudit]);

  async function handleSendStaffInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!staffEmail.trim()) return;
    setStaffSending(true);
    setStaffSendError('');
    setStaffSendSuccess('');
    setStaffInviteLink('');
    setStaffLinkCopied(false);
    try {
      const res = await api.staffInvite.send({ email: staffEmail.trim(), role: staffRole });
      setStaffSendSuccess(`Meghívó elküldve: ${staffEmail.trim()}`);
      setStaffInviteLink(res.inviteLink);
      setStaffEmail('');
      void loadStaffInvites(staffStatusFilter);
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message ?? 'A meghívó küldése sikertelen.';
      setStaffSendError(msg);
    } finally {
      setStaffSending(false);
    }
  }

  const td: React.CSSProperties = { padding: '7px 12px', fontSize: 13, verticalAlign: 'middle' };
  const th: React.CSSProperties = { ...td, fontWeight: 700, color: '#6b7280', borderBottom: '2px solid #e5e7eb', background: '#f9fafb', padding: '8px 12px' };

  const TABS: [Tab, string][] = [
    ['invites', 'Céges meghívók'],
    ['staff',   'Munkatárs meghívása'],
    ['audit',   'Regisztrációs napló'],
  ];

  return (
    <AdminLayout>
      <div style={{ padding: 28, maxWidth: 2000 }}>
        <h1 style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 700 }}>Meghívók és regisztrációs napló</h1>
        <p style={{ margin: '0 0 20px', fontSize: 13, color: '#6b7280' }}>
          Cégek által küldött meghívók, munkatársi meghívók, és a regisztrációs eseménylap.
        </p>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 2, marginBottom: 20, borderBottom: '2px solid #e5e7eb' }}>
          {TABS.map(([t, label]) => (
            <button key={t} onClick={() => setTab(t)}
              style={{
                padding: '8px 20px', fontSize: 13, fontWeight: tab === t ? 700 : 400,
                background: 'none', border: 'none', cursor: 'pointer',
                borderBottom: tab === t ? '2px solid #0ea5e9' : '2px solid transparent',
                color: tab === t ? '#0ea5e9' : '#374151', marginBottom: -2,
              }}>
              {label}
            </button>
          ))}
        </div>

        {error && (
          <div style={{ marginBottom: 16, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, color: '#dc2626', fontSize: 13 }}>
            {error}
          </div>
        )}

        {/* Company invites tab */}
        {tab === 'invites' && (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: '#6b7280' }}>Szűrés:</span>
              {(['all', 'pending', 'accepted', 'expired'] as const).map((s) => (
                <button key={s} onClick={() => setStatusFilter(s)}
                  style={{
                    padding: '4px 12px', fontSize: 12, borderRadius: 20, cursor: 'pointer',
                    background: statusFilter === s ? '#0ea5e9' : '#f3f4f6',
                    color: statusFilter === s ? '#fff' : '#374151',
                    border: 'none', fontWeight: statusFilter === s ? 700 : 400,
                  }}>
                  {s === 'all' ? 'Összes' : STATUS_LABEL[s]?.label ?? s}
                </button>
              ))}
              <span style={{ marginLeft: 'auto', fontSize: 12, color: '#9ca3af' }}>{totalInvites} bejegyzés</span>
            </div>

            {loading ? <div style={{ color: '#9ca3af', fontSize: 14 }}>Betöltés…</div> : (
              <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th style={th}>Meghívott e-mail</th>
                      <th style={th}>Cég</th>
                      <th style={th}>Meghívó</th>
                      <th style={th}>Küldve</th>
                      <th style={th}>Elfogadva</th>
                      <th style={th}>Státusz</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invites.length === 0 && (
                      <tr><td colSpan={6} style={{ ...td, textAlign: 'center', color: '#9ca3af', padding: 24 }}>Nincs meghívó.</td></tr>
                    )}
                    {invites.map((inv) => {
                      const st = STATUS_LABEL[inv.status] ?? { label: inv.status, color: '#374151', bg: '#f3f4f6' };
                      const isExpiredPending = inv.status === 'pending' && new Date(inv.expiresAt) < new Date();
                      return (
                        <tr key={inv.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ ...td, fontWeight: 600 }}>{inv.inviteeEmail}</td>
                          <td style={td}>{inv.companyName}</td>
                          <td style={{ ...td, color: '#6b7280' }}>{inv.invitedByEmail ?? '—'}</td>
                          <td style={{ ...td, color: '#6b7280' }}>{fmt(inv.createdAt)}</td>
                          <td style={{ ...td, color: '#6b7280' }}>{inv.acceptedAt ? fmt(inv.acceptedAt) : '—'}</td>
                          <td style={td}>
                            <span style={{
                              padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                              background: isExpiredPending ? '#f3f4f6' : st.bg,
                              color: isExpiredPending ? '#6b7280' : st.color,
                            }}>
                              {isExpiredPending ? 'Lejárt' : st.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Staff invites tab */}
        {tab === 'staff' && (
          <>
            {/* Send form */}
            <div style={{
              marginBottom: 24, padding: 20, background: '#f8fafc',
              border: '1px solid #e2e8f0', borderRadius: 10,
            }}>
              <h2 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700 }}>Új munkatárs meghívása</h2>
              <form onSubmit={(e) => void handleSendStaffInvite(e)}
                style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div style={{ flex: '1 1 400px' }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, color: '#374151' }}>
                    E-mail cím
                  </label>
                  <input
                    type="email"
                    required
                    value={staffEmail}
                    onChange={(e) => setStaffEmail(e.target.value)}
                    placeholder="munkatars@example.com"
                    style={{
                      width: '100%', padding: '8px 10px', fontSize: 13,
                      border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div style={{ flex: '0 0 180px' }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, color: '#374151' }}>
                    Szerepkör
                  </label>
                  <select
                    value={staffRole}
                    onChange={(e) => setStaffRole(e.target.value as 'tenant_admin' | 'reviewer' | 'editor')}
                    style={{
                      width: '100%', padding: '8px 10px', fontSize: 13,
                      border: '1px solid #d1d5db', borderRadius: 6,
                    }}
                  >
                    <option value="reviewer">Bíráló</option>
                    <option value="tenant_admin">Adminisztrátor</option>
                    <option value="editor">Szerkesztő</option>
                  </select>
                </div>
                <button
                  type="submit"
                  disabled={staffSending}
                  style={{
                    padding: '8px 20px', background: 'var(--primary)', color: '#fff',
                    border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 13,
                    cursor: staffSending ? 'default' : 'pointer', opacity: staffSending ? 0.6 : 1,
                    flexShrink: 0,
                  }}
                >
                  {staffSending ? 'Küldés…' : 'Meghívó küldése'}
                </button>
              </form>
              {staffSendError && (
                <div style={{ marginTop: 10, padding: '8px 12px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, color: '#dc2626', fontSize: 12 }}>
                  {staffSendError}
                </div>
              )}
              {staffSendSuccess && (
                <div style={{ marginTop: 10, padding: '12px 14px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, color: '#166534', fontSize: 12 }}>
                  <div style={{ marginBottom: staffInviteLink ? 8 : 0 }}>✓ {staffSendSuccess}</div>
                  {staffInviteLink && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                      <input
                        readOnly
                        value={staffInviteLink}
                        style={{
                          flex: 1, padding: '6px 10px', fontSize: 12, fontFamily: 'monospace',
                          border: '1px solid #86efac', borderRadius: 4, background: '#fff',
                          color: '#374151', outline: 'none',
                        }}
                        onFocus={(e) => e.target.select()}
                      />
                      <button
                        onClick={() => {
                          void navigator.clipboard.writeText(staffInviteLink);
                          setStaffLinkCopied(true);
                          setTimeout(() => setStaffLinkCopied(false), 2000);
                        }}
                        style={{
                          padding: '6px 12px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
                          background: staffLinkCopied ? '#16a34a' : '#166534', color: '#fff',
                          border: 'none', borderRadius: 4, cursor: 'pointer', flexShrink: 0,
                        }}
                      >
                        {staffLinkCopied ? '✓ Másolva' : 'Link másolása'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Staff invite list */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: '#6b7280' }}>Szűrés:</span>
              {(['all', 'pending', 'accepted', 'expired'] as const).map((s) => (
                <button key={s} onClick={() => setStaffStatusFilter(s)}
                  style={{
                    padding: '4px 12px', fontSize: 12, borderRadius: 20, cursor: 'pointer',
                    background: staffStatusFilter === s ? '#0ea5e9' : '#f3f4f6',
                    color: staffStatusFilter === s ? '#fff' : '#374151',
                    border: 'none', fontWeight: staffStatusFilter === s ? 700 : 400,
                  }}>
                  {s === 'all' ? 'Összes' : STATUS_LABEL[s]?.label ?? s}
                </button>
              ))}
              <span style={{ marginLeft: 'auto', fontSize: 12, color: '#9ca3af' }}>{totalStaff} bejegyzés</span>
            </div>

            {loading ? <div style={{ color: '#9ca3af', fontSize: 14 }}>Betöltés…</div> : (
              <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th style={th}>E-mail</th>
                      <th style={th}>Szerepkör</th>
                      <th style={th}>Meghívta</th>
                      <th style={th}>Küldve</th>
                      <th style={th}>Lejárat</th>
                      <th style={th}>Elfogadva</th>
                      <th style={th}>Státusz</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staffInvites.length === 0 && (
                      <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: '#9ca3af', padding: 24 }}>Nincs munkatársi meghívó.</td></tr>
                    )}
                    {staffInvites.map((inv) => {
                      const st = STATUS_LABEL[inv.status] ?? { label: inv.status, color: '#374151', bg: '#f3f4f6' };
                      const isExpiredPending = inv.status === 'pending' && new Date(inv.expiresAt) < new Date();
                      return (
                        <tr key={inv.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ ...td, fontWeight: 600 }}>{inv.email}</td>
                          <td style={td}>{ROLE_LABELS[inv.role] ?? inv.role}</td>
                          <td style={{ ...td, color: '#6b7280' }}>{inv.invitedByEmail ?? '—'}</td>
                          <td style={{ ...td, color: '#6b7280' }}>{fmt(inv.createdAt)}</td>
                          <td style={{ ...td, color: '#6b7280' }}>{fmt(inv.expiresAt)}</td>
                          <td style={{ ...td, color: '#6b7280' }}>{inv.acceptedAt ? fmt(inv.acceptedAt) : '—'}</td>
                          <td style={td}>
                            <span style={{
                              padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                              background: isExpiredPending ? '#f3f4f6' : st.bg,
                              color: isExpiredPending ? '#6b7280' : st.color,
                            }}>
                              {isExpiredPending ? 'Lejárt' : st.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Audit log tab */}
        {tab === 'audit' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
              <span style={{ fontSize: 12, color: '#9ca3af' }}>{totalAudit} esemény</span>
            </div>

            {loading ? <div style={{ color: '#9ca3af', fontSize: 14 }}>Betöltés…</div> : (
              <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th style={th}>Esemény</th>
                      <th style={th}>Cél e-mail</th>
                      <th style={th}>Adószám</th>
                      <th style={th}>Végrehajtó</th>
                      <th style={th}>IP</th>
                      <th style={th}>Idő</th>
                      <th style={{ ...th, width: 60 }}>OK?</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLog.length === 0 && (
                      <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: '#9ca3af', padding: 24 }}>Nincs esemény.</td></tr>
                    )}
                    {auditLog.map((e) => (
                      <tr key={e.id} style={{ borderBottom: '1px solid #f3f4f6', background: e.success ? '#fff' : '#fff5f5' }}>
                        <td style={{ ...td, fontWeight: 600 }}>{EVENT_LABEL[e.eventType] ?? e.eventType}</td>
                        <td style={td}>{e.targetEmail ?? '—'}</td>
                        <td style={{ ...td, fontFamily: 'monospace' }}>{e.taxId ?? '—'}</td>
                        <td style={{ ...td, color: '#6b7280' }}>{e.actorEmail ?? '—'}</td>
                        <td style={{ ...td, color: '#9ca3af', fontFamily: 'monospace', fontSize: 11 }}>{e.ipAddress ?? '—'}</td>
                        <td style={{ ...td, color: '#6b7280' }}>{fmt(e.createdAt)}</td>
                        <td style={{ ...td, textAlign: 'center' }}>
                          {e.success
                            ? <span style={{ color: '#16a34a', fontWeight: 700 }}>✓</span>
                            : <span style={{ color: '#dc2626', fontWeight: 700 }} title={e.note ?? ''}>✗</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
